import React, { useMemo } from 'react'
import { useTheme } from '@mui/material'
import { IGridCage, IGridEdgeLine, IGridSide } from 'src/generated-types/grid-expansion'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import ExpandCanvasLine, { CanvasColours } from './ExpandCanvasLine'
import {
  CORNER_SIZE,
  LabelSpec,
  baseLabel,
  canvasLayout,
  parseBox,
  placeLabels,
  readDrawing,
  zoomBox,
} from './expandGridCanvas.action'

const ARROWS: { side: IGridSide; glyph: string }[] = [
  { side: IGridSide.Left, glyph: '←' },
  { side: IGridSide.Right, glyph: '→' },
  { side: IGridSide.Top, glyph: '↑' },
  { side: IGridSide.Bottom, glyph: '↓' },
]

// While a line is hovered everything else steps back: the added boxes lose their fill, the other lines fade.
const BACKGROUND_OPACITY = 0.18
const OTHER_LINE_OPACITY = 0.25

interface ExpandGridCanvasProps {
  // The project drawing as the backend makes it, which the preview is laid over.
  svg?: string
  cages: IGridCage[]
  side: IGridSide
  count: number
  edgeLines: IGridEdgeLine[]
  replaced: number[]
  hoveredId?: number
  onSide: (side: IGridSide) => void
  sideLabel: (side: IGridSide) => string
  newLabel: string
  movedLabel: (name: string) => string
  removedLabel: (name: string) => string
}

// The project drawing with what the chosen side adds laid over it. Each mooring line on that side stays faded where it
// is now, and is drawn again where it goes (moved) or with a cross and the new line beside it (replaced). Hovering a
// line zooms in on it, so its old and new place are easy to tell apart.
const ExpandGridCanvas: React.FC<ExpandGridCanvasProps> = ({
  svg,
  cages,
  side,
  count,
  edgeLines,
  replaced,
  hoveredId,
  onSide,
  sideLabel,
  newLabel,
  movedLabel,
  removedLabel,
}) => {
  const theme = useTheme()
  const layout = useMemo(() => canvasLayout(cages, side, count), [cages, side, count])
  const drawing = useMemo(() => readDrawing(svg, edgeLines), [svg, edgeLines])
  const colour: CanvasColours = {
    primary: getThemeColor(theme, MUIThemeColors.primaryMain),
    onPrimary: getThemeColor(theme, MUIThemeColors.white),
    muted: getThemeColor(theme, MUIThemeColors.secondaryMain),
    added: getThemeColor(theme, MUIThemeColors.successMain),
    addedSurface: getThemeColor(theme, MUIThemeColors.successLight),
    addedText: getThemeColor(theme, MUIThemeColors.successDark),
    removed: getThemeColor(theme, MUIThemeColors.errorMain),
    surface: getThemeColor(theme, MUIThemeColors.white),
  }

  const moved = useMemo(
    () =>
      edgeLines.flatMap(line => {
        const shape = drawing.shapes.get(line.id)
        if (!shape) return []
        const shift = layout.shiftOf(line)
        return [
          {
            line,
            shape,
            start: { x: shape.start.x + shift.x, y: shape.start.y + shift.y },
            anchor: { x: shape.anchor.x + shift.x, y: shape.anchor.y + shift.y },
            replaced: replaced.includes(line.id),
          },
        ]
      }),
    [edgeLines, drawing, layout, replaced],
  )

  const labels = useMemo(
    () =>
      placeLabels(
        moved.flatMap(({ line, shape, anchor, replaced: isReplaced }): LabelSpec[] => {
          const name = line.name ?? ''
          const at = (point: typeof anchor, kind: 'moved' | 'removed' | 'new', text: string, from = point) => ({
            key: `${line.id}-${kind}`,
            lineId: line.id,
            kind,
            text,
            from,
            ...baseLabel(point, side, kind),
          })
          return isReplaced
            ? [at(shape.anchor, 'removed', removedLabel(name)), at(anchor, 'new', newLabel)]
            : [at(anchor, 'moved', movedLabel(name))]
        }),
        side,
      ),
    [moved, side, newLabel, movedLabel, removedLabel],
  )

  const hovered = moved.find(item => item.line.id === hoveredId)
  const whole = parseBox(layout.viewBox)
  const box = hovered
    ? zoomBox(whole, [hovered.shape.start, hovered.shape.anchor, hovered.start, hovered.anchor])
    : whole
  const dimmed = hoveredId != null
  const backdrop = dimmed ? BACKGROUND_OPACITY : 1

  const labelColour = { moved: colour.addedText, removed: colour.removed, new: colour.muted }

  return (
    <svg
      viewBox={box.join(' ')}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    >
      <g dangerouslySetInnerHTML={{ __html: drawing.content }} />
      {layout.cells.map(cell => (
        <g key={`${cell.x}:${cell.y}`} opacity={backdrop}>
          <rect
            {...cell}
            fill={colour.addedSurface}
            fillOpacity={dimmed ? 0 : 0.45}
            stroke={colour.added}
            strokeWidth={2}
            strokeDasharray="8 6"
          />
          <circle
            cx={cell.x + cell.width / 2}
            cy={cell.y + cell.height / 2}
            r={40}
            fill={dimmed ? 'none' : colour.surface}
            stroke={colour.added}
            strokeWidth={3}
            strokeDasharray="6 4"
          />
          <text
            x={cell.x + cell.width / 2}
            y={cell.y + cell.height / 2 + 12}
            textAnchor="middle"
            fontSize={36}
            fontWeight={700}
            fill={colour.addedText}
          >
            +
          </text>
        </g>
      ))}
      {layout.corners.map(corner => (
        <rect
          key={`${corner.x}:${corner.y}`}
          x={corner.x - CORNER_SIZE.width / 2}
          y={corner.y - CORNER_SIZE.height / 2}
          {...CORNER_SIZE}
          fill={dimmed ? 'none' : colour.addedSurface}
          opacity={backdrop}
          stroke={colour.added}
          strokeWidth={2}
          strokeDasharray="4 3"
        />
      ))}
      {moved.map(item => (
        <ExpandCanvasLine
          key={item.line.id}
          {...item}
          hovered={item.line.id === hoveredId}
          opacity={dimmed && item.line.id !== hoveredId ? OTHER_LINE_OPACITY : 1}
          colour={colour}
        />
      ))}
      {labels.map(label => {
        const isHovered = label.lineId === hoveredId
        return (
          <g key={label.key} opacity={dimmed && !isHovered ? OTHER_LINE_OPACITY : 1}>
            {label.displaced && (
              <line
                x1={label.from.x}
                y1={label.from.y}
                x2={label.x}
                y2={label.y - 5}
                stroke={labelColour[label.kind]}
                strokeWidth={1}
                opacity={0.6}
              />
            )}
            <text
              x={label.x}
              y={label.y}
              textAnchor={label.textAnchor}
              fontSize={isHovered ? 19 : 16}
              fontWeight={isHovered ? 700 : 600}
              fontFamily="Verdana"
              fill={labelColour[label.kind]}
              textDecoration={label.kind === 'removed' ? 'line-through' : undefined}
              paintOrder="stroke"
              stroke={colour.surface}
              strokeWidth={4}
              strokeLinejoin="round"
            >
              {label.text}
            </text>
          </g>
        )
      })}
      {!hovered &&
        ARROWS.map(({ side: value, glyph }) => {
          const at = layout.arrows[value]
          const active = value === side
          return (
            <g
              key={value}
              role="button"
              tabIndex={0}
              aria-label={sideLabel(value)}
              aria-pressed={active}
              style={{ cursor: 'pointer' }}
              onClick={() => onSide(value)}
              onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && onSide(value)}
            >
              <circle
                cx={at.x}
                cy={at.y}
                r={26}
                fill={active ? colour.primary : colour.surface}
                stroke={colour.primary}
                strokeWidth={2}
              />
              <text
                x={at.x}
                y={at.y + 9}
                textAnchor="middle"
                fontSize={28}
                fontWeight={700}
                fill={active ? colour.onPrimary : colour.primary}
              >
                {glyph}
              </text>
            </g>
          )
        })}
    </svg>
  )
}

export default ExpandGridCanvas
